import React, { useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import { EditorState, Modifier, RichUtils } from 'draft-js';
import Editor from 'draft-js-plugins-editor';
import { MdFormatBold, MdFormatItalic, MdFormatUnderlined, MdLink } from 'react-icons/md';
import { BsPaperclip } from 'react-icons/bs';
import { createLinkDecorator } from './plugins/linkPlugin';
import { Button, Card } from 'react-bootstrap';
import { icons } from '../../assets';

import 'draft-js/dist/Draft.css';
import './RichEditor.scss';
import { removeExifFromMultipleFiles } from '../../helpers/jsHelper';
import { ACCEPTABLE_IMAGE_TYPE } from '../../constants/constants';

const { CloseIconSmall } = icons;

const RichEditor = ({
  editorState: value,
  onEditorStateChange,
  onEditorBlur,
  placeholder = 'Write a message',
  editorStyles,
  attachments = [],
  onAttachmentsChange,
  onAttachmentRemove,
}) => {
  const editorRef = useRef(null);
  const imageUploadRef = useRef(null);
  const [editorState, setEditorState] = useState(EditorState.createEmpty());

  useEffect(() => {
    if (value) {
      setEditorState(value);
    }
  }, [value]);

  const handleKeyCommand = (command, editorState) => {
    const newState = RichUtils.handleKeyCommand(editorState, command);
    if (newState) {
      setEditorState(newState);
      return 'handled';
    }
    return 'not-handled';
  };

  function _onBoldClick() {
    setEditorState(RichUtils.toggleInlineStyle(editorState, 'BOLD'));
  }

  function _onItalicClick() {
    setEditorState(RichUtils.toggleInlineStyle(editorState, 'ITALIC'));
  }

  function _onUnderlineClick() {
    setEditorState(RichUtils.toggleInlineStyle(editorState, 'UNDERLINE'));
  }

  function _onAddLinkClick() {
    const selection = editorState.getSelection();
    if (selection.isCollapsed()) return;

    const link = window.prompt('Paste the link -');
    if (!link) return;

    const decorator = createLinkDecorator();

    const content = editorState.getCurrentContent();
    const contentWithEntity = content.createEntity('LINK', 'MUTABLE', {
      url: link,
    });
    const entityKey = contentWithEntity.getLastCreatedEntityKey();
    const textWithEntity = Modifier.applyEntity(content, selection, entityKey);
    const newState = EditorState.createWithContent(textWithEntity, decorator);
    onEditorStateChange(newState);
  }

  const onChange = async (event) => {
    const files = await removeExifFromMultipleFiles(event.target.files);
    onAttachmentsChange(files);
  }

  const currentInlineStyle = editorState.getCurrentInlineStyle();

  return (
    <div className="RichEditor">
      <div
        className={cx('DraftEditor-wrapper')}
        style={editorStyles}
        onClick={() => {
          editorRef.current && editorRef.current.focus();
        }}>
        <div className="DraftEditor-container">
          <Editor
            ref={editorRef}
            placeholder={placeholder}
            editorState={editorState}
            onChange={onEditorStateChange}
            onBlur={onEditorBlur}
            handleKeyCommand={handleKeyCommand}
          />
        </div>
        <div className="DraftEditor-attachmentContainer">
          <input
            ref={imageUploadRef}
            type="file"
            accept={ACCEPTABLE_IMAGE_TYPE}
            onClick={(event) => {
              event.target.value = '';
            }}
            onChange={onChange}
            multiple
            hidden
          />
          {attachments.map((attachment) => (
            <Card className="public-DraftEditor-attachment">
              <Card.Img variant="top" src={attachment.src} alt="attachment_image" />
              <Card.ImgOverlay>
                <Button
                  onClick={() => onAttachmentRemove(attachment.id)}
                  className="modalCloseButton lf-modal-close-button">
                  <CloseIconSmall />
                </Button>
              </Card.ImgOverlay>
              <Card.Body>
                <Card.Text className="mx-1 ellipsis">{attachment.file.name}</Card.Text>
              </Card.Body>
            </Card>
          ))}
        </div>
      </div>
      <div className="DraftEditor-toolbar">
        <div
          className={cx('public-DraftEditor-action', currentInlineStyle.has('BOLD') && 'active')}
          onMouseDown={(e) => e.preventDefault()}
          onClick={_onBoldClick}>
          <MdFormatBold />
        </div>
        <div
          className={cx('public-DraftEditor-action', currentInlineStyle.has('ITALIC') && 'active')}
          onMouseDown={(e) => e.preventDefault()}
          onClick={_onItalicClick}>
          <MdFormatItalic />
        </div>
        <div
          className={cx(
            'public-DraftEditor-action',
            currentInlineStyle.has('UNDERLINE') && 'active'
          )}
          onMouseDown={(e) => e.preventDefault()}
          onClick={_onUnderlineClick}>
          <MdFormatUnderlined />
        </div>
        <div
          className={cx('public-DraftEditor-action')}
          onMouseDown={(e) => e.preventDefault()}
          onClick={_onAddLinkClick}>
          <MdLink />
        </div>
        <div
          className={cx('public-DraftEditor-action')}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            imageUploadRef.current && imageUploadRef.current.click();
          }}>
          <BsPaperclip />
        </div>
      </div>
    </div>
  );
};

export default RichEditor;
